/**
 * API layer for the Prime Corporate Services booking pages and their activity trail
 * (backend app `prime_booking`, mounted at `/api/prime-booking/`).
 *
 * The guest page is public, so the token is sent only when one exists: a signed-in
 * viewer opening the guest page without `?agent=` gets their own context, an anonymous
 * one gets `referral_required`.
 */

import type {
  ActivityFilters,
  ActivityPage,
  BookingContext,
  BrowserEventBody,
  PrimeBookingAccess,
  PrimeBookingErrorBody,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const BASE = '/api/prime-booking';

/** Non-2xx response. `message` is the backend's user-safe `detail`. */
export class PrimeBookingApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'PrimeBookingApiError';
    this.status = status;
    this.code = code;
  }
}

function headers(): HeadersInit {
  const token = localStorage.getItem('wb.authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${BASE}${path}`, { ...init, headers: headers() });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Partial<PrimeBookingErrorBody> | null;
    throw new PrimeBookingApiError(
      body?.detail || 'Scheduling is temporarily unavailable. Please try again.',
      response.status,
      body?.code || 'unavailable',
    );
  }
  return (await response.json()) as T;
}

export function fetchMemberContext(signal?: AbortSignal): Promise<BookingContext> {
  return request('/member/', { signal });
}

/** Every `agent` value is forwarded as-is; the backend refuses a repeated one. */
export function fetchGuestContext(agentCodes: string[], signal?: AbortSignal): Promise<BookingContext> {
  const query = new URLSearchParams();
  agentCodes.forEach((code) => query.append('agent', code));
  const suffix = query.toString() ? `?${query}` : '';
  return request(`/guest/${suffix}`, { signal });
}

/**
 * Record an event the page saw. Fire-and-forget: a lost trail row must never
 * disturb the booking, so failures are swallowed. `keepalive` lets a post made
 * just before navigation still complete.
 */
export async function recordBookingEvent(body: BrowserEventBody): Promise<void> {
  try {
    await request('/events/', { method: 'POST', body: JSON.stringify(body), keepalive: true });
  } catch {
    // Intentionally ignored — see above.
  }
}

export function fetchPrimeBookingAccess(signal?: AbortSignal): Promise<PrimeBookingAccess> {
  return request('/my-access/', { signal });
}

export function fetchActivity(filters: ActivityFilters, page: number, signal?: AbortSignal): Promise<ActivityPage> {
  const query = new URLSearchParams({ page: String(page) });
  if (filters.agent_code.trim()) query.set('agent_code', filters.agent_code.trim());
  if (filters.smd_code.trim()) query.set('smd_code', filters.smd_code.trim());
  if (filters.event) query.set('event', filters.event);
  if (filters.source) query.set('source', filters.source);
  if (filters.from) query.set('from', filters.from);
  if (filters.to) query.set('to', filters.to);
  if (filters.include_archived) query.set('include_archived', 'true');
  return request(`/activity/?${query}`, { signal });
}
